// Swaps.jsx — DAS-2360
//
// "Swap of the day" tab content. Renders ONE card per day showing the SELL × BUY
// pair the swap engine picked (DAS-2359). If the engine found no eligible pair,
// shows an empty-state banner with the reason string from the backend.
//
// Design history: DAS-2358 (discussion). Backend: DAS-2359. This file: DAS-2360.
// Effectiveness rollup: DAS-2361 (sibling, separate ticket).
//
// Auto-mark-seen: mirrors the DAS-2350 pattern in AlertCard.jsx — fires once per
// day's row ID, only when the row's `seen != 1`, never auto-touches `marked_taken`
// or `marked_ignored` (those stay user-controlled).

import React, { useEffect, useState } from 'react';
import { Card, Button, Tag, Tooltip, Modal, Typography, Divider } from 'antd';
import {
  SwapRightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { fetchSwapOfTheDay, markSwapRecommendation, fetchSwapRecord } from '../functions/alertClauseService';

const { Text, Title } = Typography;

// Helper: format an epoch (seconds) → "Mar 10" / "May 6, 2025" depending on year
const fmtDate = (epoch) => {
  if (!epoch) return null;
  const d = new Date(Number(epoch) * 1000);
  if (Number.isNaN(d.getTime())) return null;
  const opts = d.getFullYear() === new Date().getFullYear()
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString(undefined, opts);
};

const fmtPct = (v) => {
  if (v === null || v === undefined) return '—';
  const n = parseFloat(v);
  return Number.isFinite(n) ? `${n.toFixed(1)}%` : '—';
};

const fmtBeta = (v) => {
  if (v === null || v === undefined) return '—';
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
};

// DAS-2462: Format currency
const fmtEur = (v) => {
  if (v === null || v === undefined) return '—';
  const n = parseFloat(v);
  return Number.isFinite(n) ? `€${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';
};

// Sub-component: one side of the swap (SELL or BUY). Same fields, mirrored layout.
// DAS-2462: Clickable — onClick opens the alert text deepdive.
//           Shows financial details (sell: total value, buy: shares for full swap).
const SwapSide = ({ side, ticker, alertSubtype, alertType, confidence, opportunityFactor,
                    aeUsedColumn, aeValue, aeN, beta, entryEpoch, onClick,
                    positionValue, positionShares, currentPrice, sharesForFullSwap }) => {
  const sideLabel = side === 'sell' ? 'SELL' : 'BUY';
  const sideColor = side === 'sell' ? '#cf1322' : '#3daa6e';
  const dateLabel = fmtDate(entryEpoch);
  return (
    <div
      style={{ flex: 1, padding: '0 12px', cursor: 'pointer', borderRadius: 8,
               transition: 'background 0.2s' }}
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <Tag color={sideColor} style={{ fontWeight: 'bold', fontSize: 12 }}>{sideLabel}</Tag>
      <Title level={3} style={{ marginTop: 8, marginBottom: 4 }}>{ticker || '—'}</Title>
      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
        {dateLabel ? `${dateLabel} · ` : ''}
        {alertType || '?'} / {alertSubtype || '?'}
      </Text>
      <div style={{ marginTop: 10, fontSize: 11 }}>
        <div><Text type="secondary">confidence</Text> <strong>{confidence ?? '—'}</strong></div>
        <div>
          <Text type="secondary">{aeUsedColumn || 'AE_P'}</Text>{' '}
          <strong style={{ color: aeValue > 0 ? '#3daa6e' : aeValue < 0 ? '#cf1322' : 'inherit' }}>
            {fmtPct(aeValue)}
          </strong>{' '}
          <Text type="secondary">(n={aeN ?? '—'})</Text>
        </div>
        <div><Text type="secondary">beta</Text> <strong>{fmtBeta(beta)}</strong></div>
        {opportunityFactor !== null && opportunityFactor !== undefined && (
          <div><Text type="secondary">opportunity</Text> <strong>{parseFloat(opportunityFactor).toFixed(2)}</strong></div>
        )}
        {/* DAS-2462: Financial details */}
        {side === 'sell' && positionValue != null && (
          <div style={{ marginTop: 6, borderTop: '1px solid #f0f0f0', paddingTop: 6 }}>
            <div><Text type="secondary">position</Text> <strong>{positionShares ?? '—'} shares</strong></div>
            <div><Text type="secondary">total value</Text> <strong>{fmtEur(positionValue)}</strong></div>
          </div>
        )}
        {side === 'buy' && sharesForFullSwap != null && (
          <div style={{ marginTop: 6, borderTop: '1px solid #f0f0f0', paddingTop: 6 }}>
            <div><Text type="secondary">price</Text> <strong>{fmtEur(currentPrice)}</strong></div>
            <div><Text type="secondary">full swap</Text> <strong>{sharesForFullSwap} shares</strong></div>
          </div>
        )}
      </div>
      <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 6 }}>
        click for alert details
      </Text>
    </div>
  );
};

const Swaps = ({ onUnseenChange }) => {  // DAS-2939: tab tells its badge
  const [row, setRow] = useState(null);
  const [record, setRecord] = useState(null);   // DAS-3019: the engine's own record
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // DAS-2462: Alert text deepdive modal state
  const [alertTextModal, setAlertTextModal] = useState({ open: false, side: null, text: null, ticker: null });

  // Fetch today's swap row on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [data, rec] = await Promise.all([fetchSwapOfTheDay(), fetchSwapRecord()]);
      if (!cancelled) {
        setRow(data || null);
        setRecord(rec || null);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // DAS-2360: auto-mark-seen on tab open. Same DAS-2350 pattern as AlertCard:
  // fire once per row.ID, only when seen != 1, never touch marked_taken /
  // marked_ignored (user-controlled). Soft-fail on network error.
  const rowId = row?.ID;
  const seenAlready = row?.seen === 1 || row?.seen === true;
  useEffect(() => {
    if (!rowId) return;
    if (seenAlready) { if (onUnseenChange) onUnseenChange(false); return; }  // DAS-2939
    markSwapRecommendation(rowId, 'seen')
      .then(() => { if (onUnseenChange) onUnseenChange(false); })  // DAS-2939
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowId]);

  // Click handlers for the two user-decision buttons.
  const handleMarkTaken = async () => {
    if (!rowId) return;
    const res = await markSwapRecommendation(rowId, 'taken');
    if (res?.ok) setRow({ ...row, marked_taken: 1, marked_at: new Date().toISOString() });
  };
  const handleMarkIgnored = async () => {
    if (!rowId) return;
    const res = await markSwapRecommendation(rowId, 'ignored');
    if (res?.ok) setRow({ ...row, marked_ignored: 1, marked_at: new Date().toISOString() });
  };

  // DAS-3019: one line of honesty above every card — how the engine's past picks
  // actually did. "Beat doing nothing" = the bought stock outran the sold one one
  // month after the pick (DAS-2361). Picks without a start price cannot be scored;
  // DAS-3019 stores the pick-day close so that count goes to zero.
  const recordLine = record && record.picks > 0 ? (
    <Text type="secondary" data-testid="swap-record" style={{ display: 'block', fontSize: 12, marginBottom: 10 }}>
      Record: {record.picks} picks since {fmtDate(record.first_pick_epoch) || 'the start'} ·{' '}
      {record.scorable_1m} scorable at one month, {record.beat_1m} beat doing nothing
      {record.scorable_6m ? ` · at six months ${record.beat_6m} of ${record.scorable_6m}` : ''}
      {record.missing_price ? ` · ${record.missing_price} cannot be scored (no start price)` : ''}
    </Text>
  ) : null;

  // --- Render: loading / unavailable / no-pair / pair ---

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>Loading today's swap…</div>;
  }

  // API failure or pre-cron (no row yet today) → show explanatory state.
  if (!row) {
    return (
      <Card style={{ maxWidth: 720, margin: '24px auto' }}>
        <Title level={4}>💱 No swap data available yet</Title>
        <Text type="secondary">
          The swap engine evaluates daily during the early-morning orchestrator pass.
          Today's recommendation will appear here once that run completes.
        </Text>
      </Card>
    );
  }

  // No-pair day (engine ran but no eligible pair).
  if (!row.sell_ticker || !row.buy_ticker) {
    return (
      <Card style={{ maxWidth: 720, margin: '24px auto' }}>
        <Title level={4}>💱 No swap recommendation today</Title>
        {recordLine}
        <Text style={{ display: 'block', marginTop: 8 }}>
          <strong>Reason:</strong> {row.reason || 'No eligible pair'}
        </Text>
        <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
          The swap engine evaluates every day after the alert effectiveness recompute.
          Check back tomorrow.
        </Text>
      </Card>
    );
  }

  // Real swap pair → render the card.
  const isMarkedTaken = row.marked_taken === 1 || row.marked_taken === true;
  const isMarkedIgnored = row.marked_ignored === 1 || row.marked_ignored === true;
  const alreadyDecided = isMarkedTaken || isMarkedIgnored;

  return (
    <>
      <Card
        style={{ maxWidth: 920, margin: '24px auto' }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>💱 Today's swap</span>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ({fmtDate(row.run_epoch)})
            </Text>
            <Tooltip title="The single SELL × BUY pair the swap engine picked today. Picking criteria: combined confidence is maximum among all pairs that pass opportunity ≥ 0.4, confidence ≥ 200, AE-iii positive (DAS-2358), and beta band |Δβ| ≤ 0.3 (DAS-2359).">
              <InfoCircleOutlined style={{ color: '#999' }} />
            </Tooltip>
            {isMarkedTaken && <Tag color="green" icon={<CheckCircleOutlined />}>Taken</Tag>}
            {isMarkedIgnored && <Tag color="default" icon={<CloseCircleOutlined />}>Ignored</Tag>}
          </div>
        }
        extra={
          <Button size="small" onClick={() => setDetailsOpen(true)}>View details</Button>
        }
      >
        {recordLine}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <SwapSide
            side="sell"
            ticker={row.sell_ticker}
            alertSubtype={row.sell_alert_subtype}
            alertType={row.sell_alert_type}
            confidence={row.sell_confidence}
            opportunityFactor={row.sell_opportunity_factor}
            aeUsedColumn={row.sell_ae_used_column}
            aeValue={row.sell_ae_value}
            aeN={row.sell_ae_n}
            beta={row.sell_beta}
            entryEpoch={row.run_epoch}
            positionValue={row.sell_position_value}
            positionShares={row.sell_position_shares}
            onClick={() => setAlertTextModal({
              open: true, side: 'SELL', ticker: row.sell_ticker,
              text: row.sell_alert_text
            })}
          />
          <div style={{ fontSize: 36, color: '#1890ff' }}><SwapRightOutlined /></div>
          <SwapSide
            side="buy"
            ticker={row.buy_ticker}
            alertSubtype={row.buy_alert_subtype}
            alertType={row.buy_alert_type}
            confidence={row.buy_confidence}
            opportunityFactor={row.buy_opportunity_factor}
            aeUsedColumn={row.buy_ae_used_column}
            aeValue={row.buy_ae_value}
            aeN={row.buy_ae_n}
            beta={row.buy_beta}
            entryEpoch={row.run_epoch}
            currentPrice={row.buy_current_price}
            sharesForFullSwap={row.buy_shares_for_full_swap}
            onClick={() => setAlertTextModal({
              open: true, side: 'BUY', ticker: row.buy_ticker,
              text: row.buy_alert_text
            })}
          />
        </div>
        {row.combined_score !== null && row.combined_score !== undefined && (
          <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 11, textAlign: 'center' }}>
            combined confidence: <strong>{parseFloat(row.combined_score).toFixed(1)}</strong>
          </Text>
        )}
        <Divider style={{ margin: '16px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleMarkTaken}
            disabled={alreadyDecided}
          >
            Mark as taken
          </Button>
          <Button
            icon={<CloseCircleOutlined />}
            onClick={handleMarkIgnored}
            disabled={alreadyDecided}
          >
            Mark as ignored
          </Button>
        </div>
        {alreadyDecided && (
          <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 11, marginTop: 8 }}>
            Decision recorded. Effectiveness of this swap (was it the right call?) will be measurable after the
            6-month forward window — DAS-2361.
          </Text>
        )}
      </Card>

      {/* DAS-2462: Alert text deepdive modal — shows the underlying alert card text */}
      <Modal
        title={`${alertTextModal.side || ''} — ${alertTextModal.ticker || ''} — Alert Details`}
        open={alertTextModal.open}
        onCancel={() => setAlertTextModal({ open: false, side: null, text: null, ticker: null })}
        footer={null}
        width={640}
        destroyOnClose
      >
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6, padding: '8px 0' }}>
          {alertTextModal.text || 'No alert text available.'}
        </div>
      </Modal>

      {/* Details modal — shows both underlying alert IDs + scoring inputs */}
      <Modal
        title={`Swap details — ${row.sell_ticker} → ${row.buy_ticker}`}
        open={detailsOpen}
        onCancel={() => setDetailsOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <div style={{ fontSize: 12 }}>
          <Title level={5}>SELL side</Title>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td style={{ padding: 4, color: '#666' }}>Ticker</td><td><strong>{row.sell_ticker}</strong></td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Source alert</td><td>#{row.sell_alert_id} — {row.sell_alert_type} / {row.sell_alert_subtype}</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Subtype identifier</td><td>{row.sell_alert_subtype_identifier ?? '—'}</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Confidence</td><td>{row.sell_confidence ?? '—'}</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Opportunity factor</td><td>{row.sell_opportunity_factor ?? '—'}</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>AE column used</td><td>{row.sell_ae_used_column ?? '—'}</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>AE value</td><td>{fmtPct(row.sell_ae_value)} (n={row.sell_ae_n ?? '—'})</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Position</td><td>{row.sell_position_shares ?? '—'} shares</td></tr>
              <tr><td style={{ padding: 4, color: '#666', fontWeight: 'bold' }}>Total value</td><td><strong>{fmtEur(row.sell_position_value)}</strong></td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Beta</td><td>{fmtBeta(row.sell_beta)}</td></tr>
            </tbody>
          </table>

          <Title level={5} style={{ marginTop: 16 }}>BUY side</Title>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td style={{ padding: 4, color: '#666' }}>Ticker</td><td><strong>{row.buy_ticker}</strong></td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Source alert</td><td>#{row.buy_alert_id} — {row.buy_alert_type} / {row.buy_alert_subtype}</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Subtype identifier</td><td>{row.buy_alert_subtype_identifier ?? '—'}</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Confidence</td><td>{row.buy_confidence ?? '—'}</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Opportunity factor</td><td>{row.buy_opportunity_factor ?? '—'}</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>AE column used</td><td>{row.buy_ae_used_column ?? '—'}</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>AE value</td><td>{fmtPct(row.buy_ae_value)} (n={row.buy_ae_n ?? '—'})</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Beta</td><td>{fmtBeta(row.buy_beta)}</td></tr>
              <tr><td style={{ padding: 4, color: '#666' }}>Current price</td><td>{fmtEur(row.buy_current_price)}</td></tr>
              <tr><td style={{ padding: 4, color: '#666', fontWeight: 'bold' }}>Full swap qty</td><td><strong>{row.buy_shares_for_full_swap ?? '—'} shares</strong></td></tr>
            </tbody>
          </table>

          <Divider style={{ margin: '16px 0' }} />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Pair-level: combined confidence = {parseFloat(row.combined_score ?? 0).toFixed(1)}.
            Beta delta = {Math.abs(parseFloat(row.buy_beta ?? 0) - parseFloat(row.sell_beta ?? 0)).toFixed(2)} (limit ≤ 0.30).
          </Text>
        </div>
      </Modal>
    </>
  );
};

export default Swaps;
